import { CellModel, ColumnSource, CommentItem, RequestFilters, RowSource } from "@omegagrid/grid";
import { CellData, GridEditorAdapter, GridEditorOptions, formatAccounting } from "@reporting/rp-app-host";
import { msg } from "@omegagrid/localize";
import { AccountingGroup, AccountingRights, AccountingSettings, User } from "@reporting/rp-types/core";
import { AccessRight, AccessRole } from "@reporting/rp-types/users";
import { IconSpec, actions, dom } from "@omegagrid/core";
import { render, html } from "lit";
import { renderers } from "@omegagrid/grid";
import { SerializedValue, TreeAdapter, TreeOptions, TreeSource } from "@omegagrid/tree";
import { Module, ModuleCode } from "@reporting/rp-types/accounting";
import { map } from "lit/directives/map.js";
import rp from "@reporting/rp-app-host";
import { AccessRightDetail } from "../components";
import { ACCESS_TYPE, cellRights, diffRights, groupRightsByScope, rightScope, rightType, settleRights } from "./accessRightDiff";

export type UserAccessGridOptions = {
	/** accounting (ACCOUNTING) */
	a?: string,
	/** group (ID_ACCOUNTING_GROUP) */
	g?: string,
	/** user (USER_NAME) */
	u?: string,
	/** user group (ID_ACCESS_ROLE) */
	r?: string,
}

/** scope (grid column) → the dropdown types it offers, the bare right as `access` */
const getRights = () => groupRightsByScope(Object.values(AccountingRights));

type RowType = 'accountings' | 'accounting' | 'roles' | 'users' | 'user' | 'role' | 'group';
type RowProps = {
	rowType: RowType;
	accounting?: AccountingSettings
}

const rowIcons: Record<RowType, string> = {
	accountings: 'buildings',
	accounting: 'building',
	roles: 'users',
	role: 'users',
	users: 'user',
	user: 'user',
	group: 'buildings',
}

const accessIcons: Record<string, IconSpec> = {
	'read': {icon: ['fas', 'eye'], color: '#ffe066'},
	'access': {icon: ['fas', 'circle-check'], color: '#7ed957'},
	'define': {icon: ['fas', 'gear'], color: '#7ab5e3'},
	'write': {icon: ['fas', 'pen'], color: '#ffb347'},
	'import': {icon: ['fas', 'file-import'], color: '#7ab5e3'},
	'export': {icon: ['fas', 'file-export'], color: '#7a8fe3'},
	'delete': {icon: ['fas', 'trash'], color: '#ff5c5c'},
}

const moduleRightMap: Record<string, ModuleCode> = {
	'finance': 'F',
	'items': 'I',
	'aging': 'A',
}

export class UserAccessGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];
	private _rows: RowSource[];
	private _userIndex: Map<number, Map<any, Map<string, AccessRight[]>>>;
	private _userGroupIndex: Map<string, Map<string, AccessRight>>;
	private _roleIndex: Map<number, Map<any, Map<string, AccessRight[]>>>;
	private _roleGroupIndex: Map<string, Map<string, AccessRight>>;
	private _userAdapter: TreeAdapter;
	private _roleAdapter: TreeAdapter;

	constructor(private params: UserAccessGridOptions) { super() }

	get userAdapter() {
		if (this._userAdapter) return this._userAdapter;
		return this._userAdapter = new (class extends TreeAdapter {
			constructor(private g: number) { super(); }
			async getTree(): Promise<TreeSource> {
				return (await rp.ms('users').get<User[]>(
					this.g ? `allUsers/${this.g}` : 'groupUsers',
					{ fields: ['user_name', 'login_name'] }
				)).map(user => ({
					k: user.user_name,
					v: user.login_name,
					l: 1
				}));
			}
			getOptions(): TreeOptions {
				return {selectMode: 'single'};
			}
		})(parseInt(this.params?.g));
	}

	get roleAdapter() {
		if (this._roleAdapter) return this._roleAdapter;
		return this._roleAdapter = new (class extends TreeAdapter {
			constructor(private g: number) { super(); }
			async getTree(): Promise<TreeSource> {
				return (await rp.ms('users').get<AccessRole[]>(
					'accessRoles',
					{ fields: ['id_access_role', 'role'], ...(this.g ? { g: this.g } : {}) }
				)).map(role => ({
					k: role.id_access_role.toString(),
					v: role.role,
					l: 1
				}));
			}
			getOptions(): TreeOptions {
				return {selectMode: 'single'};
			}
		})(parseInt(this.params?.g));
	}

	/**
	 * Existing rows carry the accounting in `a` and the group in `g`; save() reads those,
	 * not the `accounting` object. A new row has to carry them too, otherwise its rights
	 * are posted with a null accounting and no group, and nothing is stored.
	 */
	private addRow(cell: CellModel, rowType: 'user' | 'role', adapter: TreeAdapter) {
		const accounting = cell.row.properties.accounting as AccountingSettings;
		const newRow = this.editor.newRow(cell.r + 1);
		newRow.setProperty('rowType', rowType);
		newRow.setProperty('accounting', accounting);
		newRow.setProperty('a', accounting?.accounting);
		newRow.setProperty('g', accounting?.id_accounting_group);
		newRow.level = cell.row.level + 1;
		const cell1 = newRow.cell(0);
		cell1.type = 's';
		cell1.adapter = adapter;
		this.component.rendering.updateCell(cell1);
	}

	addUser(cell: CellModel) {
		this.addRow(cell, 'user', this.userAdapter);
	}

	addRole(cell: CellModel) {
		this.addRow(cell, 'role', this.roleAdapter);
	}

	deleteUserOrRole(cell: CellModel) {
		this.editor.deleteRow(cell.r);
	}

	/**
	 * Ticks every right of every module on the row. Inherited cells are left alone —
	 * they are not editable and their rights come from the group or a role anyway.
	 */
	selectAllRights(cell: CellModel) {
		const rights = getRights();
		this._columns.forEach((column, c) => {
			if (c < 1) return; // skip title column
			const target = cell.row.cell(c);
			if (target?.editable === false) return;
			const types = rights.get(column.data as string) ?? [];
			const value = types.map(type => [type, 0, 1]) as SerializedValue[];
			this.editor.setCellValue(cell.r, c, value as unknown as string);
		});
	}

	attached() {
		dom.appendStyle(this.component.table.shadowRoot, `
			a { color: var(--og-accent-color) !important; }
			a:hover { text-decoration: underline; cursor: pointer; }
		`, '__rp-user-access-grid-editor-style');
	}

	titleRenderer = (div: HTMLDivElement, cell: CellModel) => {
		dom.empty(div);
		const isNew = !!cell.row.getProperty('new');
		if (isNew) {
			return renderers.DropdownRenderer(div, cell);
		} else {
			div = renderers.BaseRenderer(div, cell);
		}

		const innerDiv = dom.createElement<HTMLDivElement>('div');
		const props = cell.row.properties as RowProps;
		innerDiv.style.paddingLeft = dom.px(cell.calculatedIndent ?? 4);
		innerDiv.style.display = 'flex';
		innerDiv.style.flexDirection = 'row';
		innerDiv.style.gap = '2px';
		innerDiv.style.alignItems = 'center';

		const value =
			props.rowType == 'roles' ? `${msg('User groups')} (${cell.row.children?.length ?? 0})` :
			props.rowType == 'users' ? `${msg('Users')} (${cell.row.children?.length ?? 0})` :
			props.rowType == 'accountings' ? `${msg('Companies')} (${cell.row.children?.length ?? 0})` :
			cell.displayValue || cell.value;

		render(html`
			${cell.value ? html`<og-icon icon="${rowIcons[props.rowType]}"></og-icon>` : ''}
			<div style="flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap" title="${value ?? ''}">
				${['user', 'role'].includes(props.rowType) ? html`
					<a @pointerdown="${(e: PointerEvent) => e.stopPropagation()}"
						@click="${() => actions.dispatchActionEvent(this.editor, 'editor', {
						key: 'click',
						value: {type: props.rowType, id: cell.value, value: cell.displayValue}
					})}">${value}</a>
				` : value}
			</div>
			<div style="display: flex; gap: 2px; flex-shrink: 0">
				${!this.canAdminProps(props) ? '' : ['users', 'roles'].includes(props.rowType) ? html`
					<og-button
						icon="plus"
						color="gray"
						style="height: 15px"
						title="${msg('Add')}"
						@pointerdown="${(e: PointerEvent) => e.stopPropagation()}"
						@click="${() => props.rowType == 'users' ? this.addUser(cell) : this.addRole(cell)}">
					</og-button>
				` : ['user', 'role'].includes(props.rowType) ? html`
					<og-button
						icon="check-double"
						color="gray"
						style="height: 15px"
						title="${msg('Select all')}"
						@pointerdown="${(e: PointerEvent) => e.stopPropagation()}"
						@click="${() => this.selectAllRights(cell)}">
					</og-button>
					${!cell.row.properties.new ? html`
						<og-button
							icon="trash"
							color="red"
							style="height: 15px"
							title="${msg('Remove')}"
							@pointerdown="${(e: PointerEvent) => e.stopPropagation()}"
							@click="${() => this.deleteUserOrRole(cell)}">
						</og-button>
					` : ''}
				` : ''}
			</div>
		`, innerDiv);

		div.prepend(innerDiv);
		return div;
	}

	accessRenderer = (div: HTMLDivElement, cell: CellModel) => {
		div = cell.editable ? renderers.DropdownRenderer(div, cell) : renderers.BaseRenderer(div, cell);
		const v = cell.value as SerializedValue[];
		if (!v?.length) return div;
		const innerDiv = dom.createElement<HTMLDivElement>('div');
		innerDiv.style.textAlign = 'center';
		render(map(v, item => html`
			<og-icon .icon="${accessIcons[item[0]]}"></og-icon>
		`), innerDiv);
		div.prepend(innerDiv);
	}

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		const modules = await rp.ms('accounting').get<Module[]>('modules');
		return (this._columns = [{
			data: 'title',
			w: 300,
			levelIndent: true,
			header: '',
			renderer: this.titleRenderer,
			e: true,
			priority: 1
		}, ...[...getRights().entries()].map<ColumnSource>(([scope, rights]) => ({
			w: 90,
			header: [{
				label: `${moduleRightMap[scope] ? `
					<og-icon icon="cube" style="color: ${modules.find(m => m.module === moduleRightMap[scope])?.color}"></og-icon>
				` : ''}${msg(scope)}`,
				html: true
			}],
			data: scope,
			t: 'm',
			e: true,
			priority: 1,
			renderer: this.accessRenderer,
			props: { scope },
			// content: rights.map(type => {
			// 	type = type ?? 'access';
			// 	return {k: type, v: msg(type), icon: accessIcons[type]}
			// })
			adapter: new class extends TreeAdapter {
				getTree(): TreeSource {
					return rights.map(type => ({k: type, v: msg(type), icon: accessIcons[type]}));
				}

				getOptions(): TreeOptions {
					return {
						autoClose: true
					};
				}
			}
		}))]);
	}

	indexRights(rights: AccessRight[]) {
		this._userIndex = new Map<number, Map<any, Map<string, AccessRight[]>>>();
		this._roleIndex = new Map<number, Map<any, Map<string, AccessRight[]>>>();
		this._userGroupIndex = new Map<string, Map<string, AccessRight>>();
		this._roleGroupIndex = new Map<string, Map<string, AccessRight>>();
		rights.forEach(right => {
			const a = right.accounting ?? parseInt(this.params.a);
			const u = right.user_name ?? this.params.u;
			const r = right.id_access_role ?? this.params.r;
			const ur = (u ?? r).toString();

			const map1 = r ? this._roleIndex : this._userIndex;
			if (!map1.has(a)) map1.set(a, new Map());
			const map2 = map1.get(a);
			if (!map2.has(ur)) map2.set(ur, new Map());
			const map3 = map2.get(ur);

			const rightGroup = rightScope(right.right);
			if (!map3.has(rightGroup)) map3.set(rightGroup, []);
			map3.get(rightGroup).push(right);

			if (right.source == 'G') {
				// rights set on accounting group applied on all accountings
				const map4 = r ? this._roleGroupIndex : this._userGroupIndex;
				if (!map4.has(rightGroup)) map4.set(rightGroup, new Map());
				const map5 = map4.get(rightGroup);
				map5.set(right.right, right);
			}
		});
	}

	isInherited(right: AccessRight, currentAccounting: number): boolean {
		return right.source != 'A' || right.id_source != currentAccounting;
	}

	/**
	 * Forces every right cell of a row read-only.
	 *
	 * It has to happen per CELL, not per row: this grid gives rows explicit priorities (0 and 2)
	 * against columns at 1, so a row-level `e: false` loses to the column's `e: true` and is silently
	 * ignored - failing open, which is the worst way for an authorization flag to fail.
	 */
	private lockRights(data: Record<string, any>) {
		(this._columns ?? []).forEach((column, c) => {
			if (c < 1) return;
			const key = column.data as string;
			data[key] = { ...(typeof data[key] === 'object' ? data[key] : {}), e: false };
		});
		return data;
	}

	/**
	 * The tree endpoint resolves the caller's admin right into IS_ADMIN on every row it returns -
	 * an ancestor row kept only for the tree structure is visible but not the caller's to edit.
	 * Strict === true so a row missing the flag fails closed, not open.
	 */
	private canAdminRow(accounting: AccountingSettings): boolean {
		return accounting.is_admin === true;
	}

	/**
	 * Same question from a row's properties, for the renderer - the add / select-all / remove buttons
	 * must not appear on a row whose rights the caller cannot change. A group-level row needs the
	 * whole group; anything hanging off a company needs that company.
	 */
	private canAdminProps(props: Record<string, any>): boolean {
		if (props.rowType == 'group') return rp.userProvider.isGroupAdmin();
		return props.accounting ? this.canAdminRow(props.accounting as AccountingSettings) : true;
	}

	async getRowsByUser(filters?: RequestFilters): Promise<RowSource[]> {
		this._rows = [];
		if (!this.params.u && !this.params.r) return this._rows;

		const [group, accountings, rights] = await Promise.all([
			rp.ms('accounting').get<AccountingGroup>('groups/mine'),
			rp.ms('accounting').get<AccountingSettings[]>('tree'),
			this.params.u
				? rp.ms('users').get<AccessRight[]>(`access/userGroupRights/${this.params.u}`)
				: rp.ms('users').get<AccessRight[]>(`access/roleGroupRights/${this.params.r}`)
		]);

		this.indexRights(rights);

		const groupIndex = this.params.r ? this._roleGroupIndex : this._userGroupIndex;
		const groupData: Record<string, any> = {
			title: {v: group.group_name, dv: group.group_name, e: false},
			...(groupIndex ? Object.fromEntries([...groupIndex.entries()].map(([scope, r]) => {
				const rights = [...r.values()];
				return [
					scope, {
						v: rights.map(right => [rightType(right.right), 0, 1]),
						props: { rights },
						comment: {text: '?'}
					}
				];
			})) : {}),
		};
		// a right granted on this row has no accounting and therefore reaches EVERY company of the
		// group - not something an administrator of only some of them may hand out
		if (!rp.userProvider.isGroupAdmin()) this.lockRights(groupData);

		this._rows.push({
			l: 0,
			t: 't',
			priority: 0,
			e: true,
			data: groupData,
			props: { rowType: 'group', group, g: group.id_group },
		});

		const index = this.params.r ? this._roleIndex : this._userIndex;
		accountings.forEach((accounting, i) => {
			const scopes = index.get(accounting.accounting)?.get(this.params.r ?? this.params.u);
			const data: Record<string, any> = {
				title: {v: accounting.accounting, dv: formatAccounting(accounting), e: false},
				...(scopes ? Object.fromEntries([...scopes.entries()].map(([scopes, rights]) => [
					scopes, {
						e: !this.isInherited(rights[0], accounting.accounting),
						v: rights.map(right => [rightType(right.right), 0, 1]),
						props: { rights },
						comment: {text: '?'}
					}
				])) : {}),
			};
			if (!this.canAdminRow(accounting)) this.lockRights(data);

			this._rows.push({
				l: accounting.level + 1,
				priority: 0,
				e: true,
				data,
				props: { rowType: 'accounting', accounting, a: accounting.accounting, g: accounting.id_accounting_group },
			});
		});

		return this._rows;
	}

	async getRowsByAccounting(filters?: RequestFilters): Promise<RowSource[]> {
		const [accountings, rights] = await Promise.all([
			rp.ms('accounting').get<AccountingSettings[]>(
				`tree${this.params.g ? `/${this.params.g}` : ''}`,
				this.params.a ? { a: this.params.a } : {}
			),
			rp.ms('users').get<AccessRight[]>(
				this.params.a ? `access/accountingRights/${this.params.a}` : `access/groupRights${this.params.g ? `/${this.params.g}` : ''}`
			)
		]);

		this.indexRights(rights);
		this._rows = [];
		accountings.forEach((accounting, i) => {
			let level = accounting.level * 2;
			const canAdmin = this.canAdminRow(accounting);

			if (i > 0 && accountings[i - 1].level < accounting.level) {
				this._rows.push({
					l: level - 1,
					t: 't',
					priority: 2,
					e: false,
					data: {title: {v: 'companies'}},
					props: { rowType: 'accountings' },
				});
			}

			this._rows.push({
				l: level,
				t: 't',
				priority: 2,
				e: false,
				data: {title: {v: accounting.accounting, dv: accounting.company}},
				props: { rowType: 'accounting', accounting },
			});

			this._rows.push({
				data: {title: {v: 'roles'}},
				t: 't',
				priority: 1,
				e: false,
				l: level + 1,
				props: { rowType: 'roles', accounting },
			});

			if (this._roleIndex.has(accounting.accounting)) {
				const roles = this._roleIndex.get(accounting.accounting)!;
				roles.forEach((rightGroups, r: number) => {
					const accessRight = rightGroups.values().next().value[0];
					const data: Record<string, any> = {
						title: {v: accessRight.id_access_role, dv: accessRight.role, e: false},
						...Object.fromEntries([...rightGroups.entries()].map(([rightGroup, rights]) => [
							rightGroup, {
								e: !this.isInherited(rights[0], accounting.accounting),
								v: rights.map(right => [rightType(right.right), 0, 1]),
								props: { rights },
								comment: {text: '?'}
							}
						]))
					};
					if (!canAdmin) this.lockRights(data);

					this._rows.push({
						l: level + 2,
						data,
						props: { rowType: 'role', accounting, a: accounting.accounting, g: accounting.id_accounting_group, r: accessRight.id_access_role }
					});
				});
			}

			this._rows.push({
				data: {title: {v: 'users'}},
				t: 't',
				priority: 2,
				e: false,
				l: level + 1,
				props: { rowType: 'users', accounting }
			});

			if (this._userIndex.has(accounting.accounting)) {
				const users = this._userIndex.get(accounting.accounting);
				users.forEach((rightGroups, u: string) => {
					const accessRight = rightGroups.values().next().value[0];
					const data: Record<string, any> = {
						title: {v: accessRight.user_name, dv: accessRight.login_name, e: false},
						...Object.fromEntries([...rightGroups.entries()].map(([rightGroup, rights]) => [
							rightGroup, {
								e: !this.isInherited(rights[0], accounting.accounting),
								v: rights.map(right => [rightType(right.right), 0, 1]),
								props: { rights },
								comment: {text: '?'}
							}
						]))
					};
					if (!canAdmin) this.lockRights(data);

					this._rows.push({
						l: level + 2,
						data,
						props: { rowType: 'user', accounting, a: accounting.accounting, g: accounting.id_accounting_group, u: accessRight.user_name }
					});
				});
			}
		});

		return this._rows;
	}

	async getRows(filters?: RequestFilters): Promise<RowSource[]> {
		if (this.params.u || this.params.r) {
			return this.getRowsByUser(filters);
		} else {
			return this.getRowsByAccounting(filters);
		}
	}

	async getOptions(): Promise<Partial<GridEditorOptions>> {
		return this.createOptions({
			refreshAfterSave: true,
			colLetters: false,
			rowNumbers: false,
			columnSorting: true,
			filters: true,
			pageSize: 0,
			saveMode: 'edited_cells',
			comments: {
				renderer: (div: HTMLDivElement, comment: CommentItem, cell: CellModel) => {
					dom.empty(div);
					div.style.padding = '0';
					div.append(dom.createElement<AccessRightDetail>('rp-access-right-detail', { cell }));
					return div;
				},
			},
			toolbar: {
				new: {enabled: false},
			}
		});
	}


	/**
	 * On a new row the user or group has just been picked in the title cell and was never
	 * written to the row props, so `u`/`r` are still empty — fall back to that cell.
	 * `right` is a stored name (see accessRightDiff.ts); nothing is renamed here.
	 */
	private cellDataToAccessRight(cell: Partial<CellData>, right: string): AccessRight {
		const row = this.editor.grid.model.rows[cell.r];
		const picked = row?.properties?.new ? row.cell(0)?.value : null;
		const rowType = row?.properties?.rowType;

		return {
			accounting: cell.props.a ? cell.props.a as number : this.params.a ? parseInt(this.params.a) : null,
			id_accounting_group: cell.props.g as number,
			id_access_role: cell.props.r ? parseInt(cell.props.r as string) : this.params.r ? parseInt(this.params.r) :
				picked && rowType === 'role' ? parseInt(picked as string) : null,
			user_name: cell.props.u ? cell.props.u as string : this.params.u ? this.params.u :
				picked && rowType === 'user' ? picked as string : null,
			right,
		};
	}

	/**
	 * Whether the right is stored on this very row: a group-level right on the group row, a right
	 * of this very company on a company, user or role row. Anything else is shown for information
	 * only (it comes from the group, a user group or a parent company) and is not revoked from
	 * here - the revoke would not match a stored row anyway.
	 */
	private isOwnRight(right: AccessRight, props: Record<string, unknown>): boolean {
		if (props.rowType == 'group') return right.source == 'G';
		const accounting = (props.a ?? (props.accounting as AccountingSettings)?.accounting) as number;
		return !this.isInherited(right, accounting);
	}

	/**
	 * Posts the difference between what the server sent (the `rights` cell props, in stored naming)
	 * and what the edited cells hold now (dropdown items, see accessRightDiff.ts). Revokes go first:
	 * should the second request fail, the subject holds fewer rights than intended rather than
	 * more, and the retry from the kept edits converges either way.
	 */
	async save(cellData: CellData[]) {
		const rightsByScope = getRights();
		const grantRights: AccessRight[] = [];
		const revokeRights: AccessRight[] = [];
		const deletedRows = new Set<number>();
		const newRows = new Set<number>();

		cellData.forEach(cell => {
			if (cell.state === 'deleted') {
				deletedRows.add(cell.r); // handled below, from the row
				return;
			}
			const row = this.editor.grid.model.rows[cell.r];
			if (row?.properties?.new) newRows.add(cell.r);
			if (cell.c < 1) return; // title column

			const scope = cell.props.scope as string;
			const original = (cell.props.rights as AccessRight[]) ?? [];
			const { grant, revoke } = diffRights(
				original.map(right => right.right),
				cellRights(scope, cell.v, rightsByScope.get(scope) ?? [])
			);

			grant.forEach(right => grantRights.push(this.cellDataToAccessRight(cell, right)));
			revoke.forEach(right => {
				if (!original.some(item => item.right === right && this.isOwnRight(item, cell.props))) return;
				revokeRights.push(this.cellDataToAccessRight(cell, right));
			});
		});

		deletedRows.forEach(r => {
			const row = this.editor.grid.model.rows[r];
			const props = row.properties;
			row.cells.forEach(cell => {
				if (!cell || cell.c < 1) return; // skip title column
				((cell.getProperty('rights') as AccessRight[]) ?? [])
					.filter(right => this.isOwnRight(right, props))
					.forEach(right => revokeRights.push(
						this.cellDataToAccessRight({r, c: cell.c, props} as Partial<CellData>, right.right)
					));
			});
			const access = this.cellDataToAccessRight({r, c: 1, props} as Partial<CellData>, ACCESS_TYPE);
			if (access.user_name || access.id_access_role) revokeRights.push(access);
		});

		newRows.forEach(r => {
			const row = this.editor.grid.model.rows[r];
			const picked = row.cell(0)?.value;
			if (!picked) return;
			const right = this.cellDataToAccessRight({r, c: 1, props: row.properties} as Partial<CellData>, ACCESS_TYPE);
			if (!right.user_name && !right.id_access_role) return;
			grantRights.push(right);
		});

		const { grant, revoke } = settleRights(grantRights, revokeRights);

		if (revoke.length) {
			await rp.ms('users').post<AccessRight[]>('access/revoke', revoke);
		}

		if (grant.length) {
			await rp.ms('users').post<AccessRight[]>('access/grant', grant);
		}
	}

}
